import * as React from "react";
import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "@phosphor-icons/react";

import { cn } from "../../lib/utils";
import { resolveRender } from "../../lib/resolve-render";
import { layerStyle } from "../../lib/layer-style";
import { cva } from "class-variance-authority";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "./resizable";

type SheetProps = Omit<SheetPrimitive.Root.Props, "children"> & {
  /** Narrowed from Base UI's payload-render union; see `dialog.tsx`. */
  children?: React.ReactNode;
};

function Sheet({ ...props }: SheetProps) {
  return <SheetPrimitive.Root {...props} />;
}

type SheetTriggerProps = Omit<SheetPrimitive.Trigger.Props, "className"> & {
  className?: string;
  asChild?: boolean;
};

function SheetTrigger({
  asChild = false,
  render,
  children,
  ...props
}: SheetTriggerProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <SheetPrimitive.Trigger
      data-slot="sheet-trigger"
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </SheetPrimitive.Trigger>
  );
}

type SheetCloseProps = Omit<SheetPrimitive.Close.Props, "className"> & {
  className?: string;
  asChild?: boolean;
};

function SheetClose({
  asChild = false,
  render,
  children,
  ...props
}: SheetCloseProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <SheetPrimitive.Close
      data-slot="sheet-close"
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </SheetPrimitive.Close>
  );
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

type SheetOverlayProps = Omit<SheetPrimitive.Backdrop.Props, "className"> & {
  className?: string;
};

/** Radix's `Overlay`. Kept under the old name — 36 files import it. */
function SheetOverlay({ className, ...props }: SheetOverlayProps) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      style={layerStyle(SHEET_Z_INDEX, className)}
      className={cn(
        "data-[open]:animate-in data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[open]:fade-in-0 fixed inset-0 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

/**
 * **1300, one below the dialog's 1400** — a deliberate ordering, not a
 * copy-paste. "TODO remove once MUI is gone".
 */
const SHEET_Z_INDEX = 1300;

const sheetContentVariants = cva(
  "bg-background data-[open]:animate-in data-[ending-style]:animate-out fixed flex flex-col shadow-lg transition ease-in-out data-[ending-style]:duration-200 data-[open]:duration-200",
  {
    variants: {
      side: {
        right:
          "data-[ending-style]:slide-out-to-right data-[open]:slide-in-from-right right-0 border-l",
        left: "data-[ending-style]:slide-out-to-left data-[open]:slide-in-from-left left-0 border-r",
        top: "data-[ending-style]:slide-out-to-top data-[open]:slide-in-from-top top-0 border-b",
        bottom:
          "data-[ending-style]:slide-out-to-bottom data-[open]:slide-in-from-bottom bottom-0 border-t",
      },
      expanded: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        side: ["right", "left"],
        class: "inset-y-0 h-full w-3/4 sm:max-w-sm",
      },
      {
        side: ["right", "left"],
        expanded: true,
        class: "max-w-3/4 sm:max-w-3/4",
      },
      {
        side: ["top", "bottom"],
        class: "inset-x-0 h-auto",
      },
    ],
    defaultVariants: {
      side: "right",
    },
  },
);

function SheetContent({
  className,
  overlayClassName,
  children,
  side = "right",
  expanded = false,
  hideCloseButton = false,
  ...props
}: Omit<SheetPrimitive.Popup.Props, "className"> & {
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  expanded?: boolean;
  /** Hide the built-in top-right close button (e.g. when the sheet supplies its own). */
  hideCloseButton?: boolean;
  /**
   * Classes for the backdrop. Needed when a sheet opens from inside a dialog:
   * sheets sit at z-1300 and dialogs at z-1400, so both the content and the
   * backdrop have to be raised or the sheet renders behind the dialog.
   */
  overlayClassName?: string;
}) {
  return (
    <SheetPortal>
      <SheetOverlay className={overlayClassName} />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        style={layerStyle(SHEET_Z_INDEX, className)}
        className={cn(sheetContentVariants({ side, expanded }), className)}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
}

/**
 * The empty panel beside a resizable sheet: clicking it closes the sheet, like
 * a backdrop. It is a pointer affordance only, so it stays out of the tab order
 * and the accessibility tree; the sheet's own close button is the named control.
 */
const SHEET_CLOSE_AREA = (
  <ResizablePanel className="pointer-events-auto">
    <SheetPrimitive.Close
      className="h-full w-full"
      tabIndex={-1}
      aria-hidden
    ></SheetPrimitive.Close>
  </ResizablePanel>
);

function SheetContentResizable({
  className,
  children,
  side = "right",
  resizableProps,
  ...props
}: Omit<SheetPrimitive.Popup.Props, "className"> & {
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  /**
   * The size of the sheet in percentage of the viewport width
   */
  size?: number;
  minSize?: number;
  /** Hide the built-in top-right close button (e.g. when the sheet supplies its own). */
  hideCloseButton?: boolean;
  /** Classes for the backdrop (see `SheetContent`). */
  overlayClassName?: string;
  resizableProps?: React.ComponentProps<typeof ResizablePanel>;
}) {
  const contentFirst = side === "left" || side === "top";
  const content = (
    <ResizablePanel
      {...resizableProps}
      className={cn("bg-background shadow-lg", resizableProps?.className)}
    >
      {children}
    </ResizablePanel>
  );
  return (
    <SheetContent
      side={side}
      className={cn(
        side === "right" || side === "left" ? "w-full" : "h-full",
        "bg-transparent border-none max-w-full sm:max-w-full shadow-none",
        className,
      )}
      {...props}
    >
      <ResizablePanelGroup
        direction={
          side === "right" || side === "left" ? "horizontal" : "vertical"
        }
      >
        {/* The content panel sits on the edge the sheet opens from. */}
        {contentFirst ? content : SHEET_CLOSE_AREA}
        <ResizableHandle withHandle />
        {contentFirst ? SHEET_CLOSE_AREA : content}
      </ResizablePanelGroup>
    </SheetContent>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-3", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-3", className)}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: Omit<SheetPrimitive.Title.Props, "className"> & { className?: string }) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: Omit<SheetPrimitive.Description.Props, "className"> & {
  className?: string;
}) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetContentResizable,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
